import { lazy } from 'react';
import { RouteObject, Navigate, useParams } from 'react-router-dom';
import MainLayout from '@/components/layouts/MainLayout';
import AgreementLayout from '@/components/layouts/AgreementLayout';
import { AuthGuard } from './components';
import TicketProcessor from './components/TicketProcessor';
import DeviceRedirect from './components/DeviceRedirect';
import { MobileGuard } from '@/components/business/MobileGuard';
import { SpaceSyncGuard } from '@/hooks/useSpaceSyncGuard';
import SilentErrorBoundary from '@/components/common/SilentErrorBoundary';
import { isMobile } from '@/utils/device';
import { dumpErrorDetail } from '@/observability/snapshot';
import type { Feature } from '@/observability/context';
import { resolveBrand } from '@/config/brand';

// 品牌默认落地路由（gxzs 站点为 /tasks 任务大厅，其余默认 /home）
const brand = resolveBrand();
const defaultRoute = brand.defaultRoute || '/home';
// 根路径是否展示全屏欢迎落地页（如 gxzs）：'/' 渲染 WelcomePage，「立即体验」按钮跳 defaultRoute
const rootShowsWelcome = brand.rootLanding === 'welcome';

// 使用React.lazy进行代码分割和懒加载
// 关键页面的预加载通过预热器和预加载器处理
const WelcomePage = lazy(() => import('@/pages/welcome'));
const LoginPage = lazy(() => import('@/pages/login'));
const LoginAccountPage = lazy(() => import('@/pages/loginAccount'));
const HomePage = lazy(() => import('@/features/home'));
const NewHomePage = lazy(() => import('@/features/newhome'));
const TextCreationPage = lazy(() => import('@/features/generation/text'));

// 其他页面使用标准懒加载
const AssetPage = lazy(() => import('@/features/assets'));
const TaskPage = lazy(() => import('@/features/tasks'));
const ImageCreationPage = lazy(() => import('@/features/generation/image'));
const VideoCreationPage = lazy(() => import('@/features/generation/video'));
const ToolsCreationPage = lazy(() => import('@/features/generation/tools'));
const AssetDetailPage = lazy(() => import('@/features/assets/detail'));
const RicegrainPage = lazy(() => import('@/features/ricegrain'));
const MembershipPage = lazy(() => import('@/features/ricegrain/components/MembershipView'));
const RiceGrainPurchasePage = lazy(
  () => import('@/features/ricegrain/components/RiceGrainPurchaseView')
);
const InvitePage = lazy(() => import('@/features/invite'));
const InviteRecordsPage = lazy(() => import('@/features/invite/records'));
const AgreementPage = lazy(() => import('@/features/agreement'));
const ReleaseNotesPage = lazy(() => import('@/features/releaseNotes'));
const UserAgreement = lazy(() => import('@/features/agreement/components/UserAgreement'));
const RiceGrainRules = lazy(() => import('@/features/agreement/components/RiceGrainRules'));
const PrivacyPolicy = lazy(() => import('@/features/agreement/components/PrivacyPolicy'));
const EnterpriseServiceAgreement = lazy(() => import('@/features/agreement/components/EnterpriseServiceAgreement'));
const UserPage = lazy(() => import('@/features/user'));
const TeamPage = lazy(() => import('@/pages/team'));
const NotFound = lazy(() => import('@/pages/404'));
// H5页面
const H5GiftClaimPage = lazy(() => import('@/pages/h5/gift'));
const H5InvitePage = lazy(() => import('@/pages/h5/invite'));
const H5HomePage = lazy(() => import('@/pages/h5/home'));
// 开发环境测试组件
const ErrorBoundaryTest = lazy(() => import('@/components/common/ErrorBoundaryTest'));
const ImageCropTest = lazy(() => import('@/pages/test/ImageCropTest'));
const CanvasPage = lazy(() => import('@/pages/canvas'));
const CanvasProjectsPage = lazy(() => import('@/pages/canvas-projects'));
const ExpertSkillSquarePage = lazy(() => import('@/features/expertSkillSquare'));
const DirectorPage = lazy(() => import('@/pages/director/main/App'));
const VideoEditingPage = lazy(() => import('@/features/videoEdit'));
const VideoEditingDetailPage = lazy(
  () => import('@/features/videoEdit/editor/Editor')
);

/**
 * 企业级路由配置
 *
 * 路由配置分为四部分：
 * 1. publicRoutes: 公共路由，无需认证即可访问，例如欢迎页、404页。
 * 2. semiProtectedRoutes: 半保护路由，无需认证即可访问，但包含侧边导航，导航项会检查登录状态。
 * 3. protectedRoutes: 受保护的路由，需要认证才能访问，通常包裹在主布局中。
 * 4. fullScreenRoutes: 全屏路由，需要认证但不包含主布局，例如资产详情页。
 *
 * 这种结构使得路由的管理更加清晰，便于扩展和维护。
 */

// 公共路由
export const publicRoutes: RouteObject[] = [
  // ── 设备自适应重定向（移动端 → H5，桌面端 → PC）───────────
  ...(isMobile()
    ? [
        { path: '/home', element: <DeviceRedirect to="/h5/home" /> },
        { path: '/invite', element: <DeviceRedirect to="/h5/invite" /> },
      ]
    : [
        { path: '/h5/home', element: <DeviceRedirect to="/home" /> },
        { path: '/h5/invite', element: <DeviceRedirect to="/invite" /> },
      ]
  ),
  {
    path: '/',
    element:
      defaultRoute !== '/home' && !rootShowsWelcome ? (
        // 品牌指定了非 /home 的默认落地（如 → /tasks 任务大厅）：根路径直达；
        // 配置了 rootLanding: 'welcome' 的品牌（如 gxzs）则展示欢迎落地页，由「立即体验」进入 defaultRoute
        <Navigate to={defaultRoute} replace />
      ) : (
        <MobileGuard>
          <WelcomePage />
        </MobileGuard>
      ),
  },
  {
    path: '/login',
    element: <Navigate to={defaultRoute} replace />,
  },
  {
    path: '/auth-login',
    element: <LoginAccountPage />,
  },
  {
    path: '/404',
    element: (
      <MobileGuard>
        <NotFound />
      </MobileGuard>
    ),
  },
  // H5页面 - 赠送算力落地页（页面内部自行处理登录逻辑）
  {
    path: '/h5/gift',
    element: <H5GiftClaimPage />,
  },
  // H5邀请好友活动页（移动端访问时保留，桌面端已在上方重定向到PC版）
  ...(isMobile()
    ? [
        { path: '/h5/invite', element: <H5InvitePage /> },
        { path: '/h5/home', element: <H5HomePage /> },
      ]
    : []
  ),
  // 裂变h5 - 重定向到 /h5/home，保留查询参数（invite_code 等）
  {
    path: '/fissionH5',
    element: <Navigate to={'/h5/home' + window.location.search} replace />,
  },
];

// 半保护路由（无需认证即可访问，但包含侧边导航，导航项会检查登录状态）
export const semiProtectedRoutes: RouteObject = {
  element: (
    <MobileGuard>
      <TicketProcessor />
      <AgreementLayout />
    </MobileGuard>
  ),
  children: [
    {
      path: '/home',
      element:
        defaultRoute !== '/home' ? (
          // 品牌指定了非 /home 的默认落地（如 gxzs → /tasks）：/home 一并重定向过去
          <Navigate to={defaultRoute} replace />
        ) : (
          <NewHomePage />
        ),
    },
    {
      path: '/tasks',
      element: <TaskPage />,
    },
    {
      path: '/canvas-projects',
      element: <CanvasProjectsPage />,
    },
    {
      path: '/release-notes',
      element: <ReleaseNotesPage />,
    },
    {
      path: '/agreement',
      element: <AgreementPage />,
      children: [
        {
          index: true,
          element: <Navigate to="/agreement/user-agreement" replace />,
        },
        {
          path: 'user-agreement',
          element: <UserAgreement />,
        },
        {
          path: 'ricegrain-rules',
          element: <RiceGrainRules />,
        },
        {
          path: 'privacy-policy',
          element: <UserAgreement />,
        },
        {
          path: 'privacy-policy-detail',
          element: <PrivacyPolicy />,
        },
        {
          path: 'enterprise-service-agreement',
          element: <EnterpriseServiceAgreement />,
        },
      ],
    },
  ],
};

// 全屏路由（需要认证但不包含主布局）

/**
 * 画布/导演页错误边界包装（P0-6）：现完全裸奔——子组件任一抛错即整页白屏，
 * 前端一条数据都收不到。用 useParams 取 canvasId（高基数，不进 extra/categories），
 * 出错时另发一条 sendLog 承载，供单条排查时查看。
 */
const CanvasRouteBoundary = ({ children }: { children: React.ReactNode }) => {
  const { canvasId } = useParams<{ canvasId: string }>();
  return (
    <SilentErrorBoundary
      feature="canvas"
      level="page"
      onError={(_error, errorInfo) => {
        dumpErrorDetail('canvas_render_error', {
          canvasId,
          componentStack: errorInfo.componentStack,
        });
      }}
    >
      {children}
    </SilentErrorBoundary>
  );
};

/**
 * 通用页面级错误边界（P1-7）
 *
 * 背景：protectedRoutes 顶层虽已有一个 SilentErrorBoundary，但存在三处不足——
 *   ① onError 只 console.error，未上报 APM（线上出错等于没数据）；
 *   ② 粒度覆盖整个布局（含 MainLayout），任一子页面出错会连带侧边栏等一起崩；
 *   ③ 未传 feature / level，异常看板无法按功能区切片。
 *
 * 做法：为高危页面就近包一层页面级边界。React 错误边界是**就近捕获**，
 * 子边界先生效，因此出错时只替换该页面内容，不会冒泡到顶层布局。
 * 同时把 feature 随异常上报，异常分析可按 feature=text/image/video/tools 切片。
 */
const FeatureRouteBoundary = ({
  feature,
  children,
}: {
  feature: Feature;
  children: React.ReactNode;
}) => (
  <SilentErrorBoundary
    feature={feature}
    level="page"
    onError={(_error, errorInfo) => {
      dumpErrorDetail(`${feature}_render_error`, {
        componentStack: errorInfo.componentStack,
      });
    }}
  >
    {children}
  </SilentErrorBoundary>
);

export const fullScreenRoutes: RouteObject[] = [
  {
    path: '/canvas/:canvasId',
    element: (
      <CanvasRouteBoundary>
        <MobileGuard>
          <AuthGuard>
            <SpaceSyncGuard>
              <CanvasPage />
            </SpaceSyncGuard>
          </AuthGuard>
        </MobileGuard>
      </CanvasRouteBoundary>
    ),
  },
  // 只读预览页：广场 Skill / 专家卡片「使用」直跳此路由（不复制），
  // 页面以 group_id 调 /canvas/detail 渲染模板，右上角「复制项目」才真正建项目。
  {
    path: '/canvas-preview/:groupId',
    element: (
      <CanvasRouteBoundary>
        <MobileGuard>
          <AuthGuard>
            <SpaceSyncGuard>
              <CanvasPage />
            </SpaceSyncGuard>
          </AuthGuard>
        </MobileGuard>
      </CanvasRouteBoundary>
    ),
  },
  {
    path: '/director/:canvasId',
    element: (
      <CanvasRouteBoundary>
        <MobileGuard>
          <AuthGuard>
            <SpaceSyncGuard>
              <DirectorPage />
            </SpaceSyncGuard>
          </AuthGuard>
        </MobileGuard>
      </CanvasRouteBoundary>
    ),
  },
];

// 受保护的路由
export const protectedRoutes: RouteObject = {
  element: (
    <SilentErrorBoundary
      onError={async (error, errorInfo) => {
        console.error('[Protected Routes] 捕获到 React 错误:', error, errorInfo);
      }}
    >
      <MobileGuard>
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      </MobileGuard>
    </SilentErrorBoundary>
  ),
  children: [
   
    {
      path: '/assets',
      element: <AssetPage />,
      children: [
        {
          path: ':id',
          element: <AssetDetailPage />,
        },
        {
          path: ':id/:modelIds',
          element: <AssetDetailPage />,
        },
      ],
    },
    {
      path: '/expert-skills',
      element: <ExpertSkillSquarePage />,
    },
    {
      path: '/creation/text',
      element: (
        <FeatureRouteBoundary feature="text">
          <TextCreationPage />
        </FeatureRouteBoundary>
      ),
    },
    {
      path: '/creation/image',
      element: (
        <FeatureRouteBoundary feature="image">
          <ImageCreationPage />
        </FeatureRouteBoundary>
      ),
    },
    {
      path: '/creation/video',
      element: (
        <FeatureRouteBoundary feature="video">
          <VideoCreationPage />
        </FeatureRouteBoundary>
      ),
    },
    {
      path: '/creation/video/:id',
      element: (
        <FeatureRouteBoundary feature="video">
          <VideoCreationPage />
        </FeatureRouteBoundary>
      ),
    },
    {
      path: '/creation/tools',
      element: (
        <FeatureRouteBoundary feature="tools">
          <ToolsCreationPage />
        </FeatureRouteBoundary>
      ),
    },
    {
      path: '/creation/edit-video',
      element: (
        <FeatureRouteBoundary feature="video">
          <VideoEditingPage />
        </FeatureRouteBoundary>
      ),
    },
    {
      path: '/editor',
      element: (
        <FeatureRouteBoundary feature="video">
          <VideoEditingDetailPage />
        </FeatureRouteBoundary>
      ),
    },
    {
      path: '/ricegrain',
      element: <RicegrainPage />,
      children: [
        {
          index: true,
          element: <Navigate to="/ricegrain/membership" replace />,
        },
        {
          path: 'membership',
          element: <MembershipPage />,
        },
        {
          path: 'purchase',
          element: <RiceGrainPurchasePage />,
        },
      ],
    },
    {
      path: '/invite',
      element: <InvitePage />,
    },
    {
      path: '/invite/records',
      element: <InviteRecordsPage />,
    },
    {
      path: '/user',
      element: <UserPage />,
    },
    // 团队管理页（V0.1 占位）：仅拥有者从用户菜单进入
    {
      path: '/team',
      element: <TeamPage />,
    },
    // 测试路由
    {
      path: '/test/error-boundary',
      element: (
          <ErrorBoundaryTest />
      ),
    },
    {
      path: '/test/image-crop',
      element: <ImageCropTest />,
    },
  ],
};
